import ManualBreadcrumb from '../../components/manual/ManualBreadcrumb';
import ManualSection from '../../components/manual/ManualSection';
import ManualAlert from '../../components/manual/ManualAlert';
import ManualStep from '../../components/manual/ManualStep';

function ManualAdolescentesPage() {
  return (
    <div className="manual-page">
      <ManualBreadcrumb items={[{ label: 'Adolescentes' }]} />
      
      <div className="manual-header">
        <h1 className="manual-title">
          <i className="bi bi-people me-3"></i>
          Trabalhando com Adolescentes
        </h1>
        <p className="manual-subtitle">
          Aprenda a cadastrar, buscar e gerenciar informações de adolescentes
        </p>
      </div>

      <ManualSection icon="bi-list-ul" title="Visualizando a Lista de Adolescentes">
        <p>Para acessar a lista de adolescentes:</p>
        
        <ManualStep number="1" title="Acesse o menu">
          No menu lateral esquerdo, clique em <strong>"Adolescentes"</strong>.
        </ManualStep>

        <ManualStep number="2" title="Visualize a lista">
          Você verá uma tabela com todos os adolescentes cadastrados, mostrando:
          <ul>
            <li><strong>Nome completo</strong></li>
            <li><strong>Data de nascimento</strong></li>
            <li><strong>Sexo</strong></li>
            <li><strong>Nome da mãe</strong></li>
            <li><strong>Ações:</strong> movimentar, visualizar, editar e excluir quando permitido</li>
          </ul>
        </ManualStep>

        <ManualAlert type="tip" title="Organização">
          A lista usa paginação e permite escolher <strong>20</strong>, <strong>50</strong> ou
          <strong> todos</strong> os registros por página. Durante atualizações, o sistema mantém
          a lista visível e indica que está carregando novos dados.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-search" title="Buscando um Adolescente">
        <p>Para encontrar rapidamente um adolescente específico:</p>
        
        <ManualStep number="1" title="Use o campo de busca">
          No topo da lista, você verá um campo com o texto <strong>"Buscar por nome..."</strong>.
          Digite o nome ou parte do nome que você está procurando.
        </ManualStep>

        <ManualStep number="2" title="A lista é filtrada automaticamente">
          Conforme você digita, a lista é atualizada automaticamente após uma pequena pausa.
          O filtro só é aplicado quando houver pelo menos <strong>2 caracteres</strong>, evitando
          consultas muito amplas.
        </ManualStep>

        <ManualStep number="3" title="Limpe a busca">
          Para ver todos os adolescentes novamente, apague o texto do campo de busca clicando
          no <strong>X</strong> que aparece dentro do campo.
        </ManualStep>

        <ManualAlert type="info">
          A busca não diferencia letras maiúsculas de minúsculas. Digite "maria" ou "MARIA" 
          que o resultado será o mesmo. A ordenação alfabética funciona tanto na lista completa 
          quanto nos resultados filtrados.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-plus-circle" title="Cadastrando um Novo Adolescente">
        <ManualAlert type="important" title="Atenção">
          Esta funcionalidade só está disponível se você tiver permissão para criar adolescentes.
          Usuários com perfil de apenas visualização não verão o botão "Novo Adolescente".
        </ManualAlert>

        <ManualStep number="1" title="Clique em 'Novo Adolescente'">
          No topo da lista de adolescentes, clique no botão verde <strong>"+ Novo Adolescente"</strong>.
        </ManualStep>

        <ManualStep number="2" title="Preencha os dados obrigatórios">
          Você verá um formulário com vários campos. Os campos com asterisco (*) são obrigatórios:
          <ul>
            <li><strong>Nome Completo:</strong> Digite o nome completo do adolescente</li>
            <li><strong>Nome Social:</strong> Informe quando houver, sem substituir o nome completo</li>
            <li><strong>Data de Nascimento:</strong> Clique no calendário ou digite no formato dia/mês/ano</li>
            <li><strong>Sexo:</strong> Masculino, Feminino ou Outros</li>
            <li><strong>Identidade de Gênero:</strong> Escolha na lista suspensa</li>
          </ul>
        </ManualStep>

        <ManualStep number="3" title="Preencha dados adicionais (opcionais)">
          Se disponível, preencha também:
          <ul>
            <li><strong>Nome da Mãe:</strong> Nome completo da mãe</li>
            <li><strong>Número da Jornada:</strong> Código único de identificação</li>
          </ul>
        </ManualStep>

        <ManualStep number="4" title="Salve o cadastro">
          Após preencher todos os dados, clique no botão verde <strong>"Salvar"</strong> no final da página.
          Se tudo estiver correto, o sistema cria o cadastro e direciona você para iniciar a
          primeira movimentação do adolescente.
        </ManualStep>

        <ManualAlert type="warning" title="Cuidado">
          Se você clicar em <strong>"Cancelar"</strong> ou sair da página sem salvar, todos os
          dados preenchidos serão perdidos.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-eye" title="Visualizando Detalhes de um Adolescente">
        <ManualStep number="1" title="Clique no ícone de visualização">
          Na lista de adolescentes, clique no ícone de olho na coluna <strong>Ações</strong>.
        </ManualStep>

        <ManualStep number="2" title="Explore as informações">
          Você verá uma janela com:
          <ul>
            <li><strong>Dados Pessoais:</strong> Nome, idade, data de nascimento, identidade de gênero</li>
            <li><strong>Informações Familiares:</strong> Nome da mãe</li>
            <li><strong>Identificação:</strong> ID Jornada, quando informado</li>
          </ul>
        </ManualStep>

        <ManualStep number="3" title="Ações disponíveis">
          Na própria lista você pode:
          <ul>
            <li>Clicar em <strong>"Movimentar"</strong> para abrir o histórico de movimentações do adolescente</li>
            <li>Clicar no <strong>lápis</strong> para editar os dados, se tiver permissão</li>
            <li>Clicar na <strong>lixeira</strong> para excluir, quando o sistema permitir</li>
          </ul>
        </ManualStep>
      </ManualSection>

      <ManualSection icon="bi-pencil" title="Editando Dados de um Adolescente">
        <ManualAlert type="important">
          Apenas usuários com permissão de edição podem alterar dados de adolescentes.
        </ManualAlert>

        <ManualStep number="1" title="Acesse a edição">
          Na página de detalhes do adolescente, clique no botão azul <strong>"Editar"</strong>.
        </ManualStep>

        <ManualStep number="2" title="Altere os dados necessários">
          O formulário aparecerá preenchido com os dados atuais. Modifique apenas o que for necessário.
        </ManualStep>

        <ManualStep number="3" title="Salve as alterações">
          Clique em <strong>"Salvar"</strong> para confirmar as mudanças. Você verá uma mensagem
          de sucesso e os dados serão atualizados.
        </ManualStep>

        <ManualAlert type="tip">
          Todas as alterações são registradas pelo sistema, incluindo quem fez e quando fez a mudança.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-arrow-left-right" title="Trabalhando com Movimentações">
        <p>
          Cada adolescente pode ter várias movimentações registradas. Uma movimentação representa
          uma entrada no sistema, com informações sobre como chegou, atendimentos recebidos e
          como saiu.
        </p>

        <h4 className="mt-4">Criando uma Nova Movimentação</h4>
        
        <ManualStep number="1" title="Acesse a tela do adolescente">
          Na página de detalhes do adolescente, clique no botão <strong>"Nova Movimentação"</strong>.
        </ManualStep>

        <ManualStep number="2" title="Preencha as informações">
          Você precisará informar:
          <ul>
            <li><strong>Situação inicial:</strong> status inicial da movimentação</li>
            <li><strong>Meio de entrada:</strong> apresentação espontânea, flagrante, MBA, flagrante + MBA, CDP, recambiamento ou outros</li>
            <li><strong>Motivo MBA:</strong> obrigatório quando o meio de entrada for MBA ou flagrante + MBA</li>
          </ul>
        </ManualStep>

        <ManualStep number="3" title="Salve a movimentação">
          Clique em <strong>"Salvar"</strong>. A movimentação será criada e você poderá
          registrar os atendimentos realizados.
        </ManualStep>

        <h4 className="mt-4">Visualizando Movimentações</h4>
        <p>
          Na página de detalhes do adolescente, role para baixo até a seção 
          <strong>"Histórico de Movimentações"</strong>. Lá você verá:
        </p>
        <ul className="manual-list">
          <li>Data e hora de cada movimentação</li>
          <li>Como o adolescente entrou no sistema</li>
          <li>Situação atual (em atendimento, disponível, não está na unidade, etc)</li>
          <li>Botão para ver detalhes completos</li>
        </ul>
      </ManualSection>

      <ManualSection icon="bi-question-circle" title="Dúvidas Comuns">
        <div className="manual-faq-item">
          <h5>Não consigo cadastrar um adolescente. Por quê?</h5>
          <p>
            Verifique se você preencheu todos os campos obrigatórios (marcados com *).
            Se mesmo assim não funcionar, pode ser que seu perfil não tenha permissão para criar cadastros.
          </p>
        </div>

        <div className="manual-faq-item">
          <h5>Posso excluir um adolescente?</h5>
          <p>
            Sim, desde que seu perfil tenha a permissão adequada e o adolescente não possua
            movimentação finalizada. Se houver movimentações não finalizadas vinculadas, o sistema
            avisa que elas também serão removidas. Movimentação finalizada bloqueia a exclusão.
          </p>
        </div>

        <div className="manual-faq-item">
          <h5>Como encontro adolescentes sem Número da Jornada?</h5>
          <p>
            Use o campo de busca normalmente. Adolescentes sem Jornada também aparecem na lista
            e podem ser encontrados pelo nome ou nome da mãe.
          </p>
        </div>

        <div className="manual-faq-item">
          <h5>O que significa "Identidade de Gênero"?</h5>
          <p>
            É como o adolescente se identifica em relação ao gênero. O sistema oferece várias
            opções para respeitar a identidade de cada pessoa.
          </p>
        </div>
      </ManualSection>

      <ManualAlert type="success" title="Próximo Passo">
        Agora que você sabe trabalhar com adolescentes, aprenda sobre 
        <strong> Movimentações</strong> para entender como registrar entradas, saídas e atendimentos em detalhes.
      </ManualAlert>
    </div>
  );
}

export default ManualAdolescentesPage;
